import type { MessageDesignTokens } from '@primeuix/themes/types/message'

export default {
  icon: {
    lg: {
      size: '1.5rem',
    },
    sm: {
      size: '1rem',
    },
    size: '1.25rem',
  },
  root: {
    borderWidth: '1px',
    borderRadius: '{content.border.radius}',
    transitionDuration: '{transition.duration}',
  },
  text: {
    lg: {
      fontSize: '1.125rem',
    },
    sm: {
      fontSize: '0.875rem',
    },
    fontSize: '1rem',
    fontWeight: '500',
  },
  simple: {
    content: {
      padding: '0',
    },
  },
  content: {
    lg: {
      padding: '0.75rem 0.875rem',
    },
    sm: {
      padding: '0.5rem 0.625rem',
    },
    gap: '0.5rem',
    padding: '0.75rem 1rem',
  },
  outlined: {
    root: {
      borderWidth: '1px',
    },
  },
  closeIcon: {
    lg: {
      size: '1.125rem',
    },
    sm: {
      size: '0.875rem',
    },
    size: '1rem',
  },
  closeButton: {
    width: '2rem',
    height: '2rem',
    focusRing: {
      style: '{focus.ring.style}',
      width: '{focus.ring.width}',
      offset: '{focus.ring.offset}',
    },
    borderRadius: '50%',
  },
  colorScheme: {
    dark: {
      info: {
        color: '{blue.500}',
        shadow: 'none',
        simple: {
          color: '{blue.500}',
        },
        outlined: {
          color: '{blue.500}',
          borderColor: '{blue.500}',
        },
        background: 'color-mix(in srgb, {blue.500}, transparent 84%)',
        borderColor: 'transparent',
        closeButton: {
          focusRing: {
            color: '{focus.ring.color}',
            shadow:
              '0 0 0 0.2rem color-mix(in srgb, {blue.500}, transparent 80%)',
          },
          hoverBackground: 'rgba(255, 255, 255, 0.05)',
        },
      },
      warn: {
        color: '{yellow.500}',
        shadow: 'none',
        simple: {
          color: '{yellow.500}',
        },
        outlined: {
          color: '{yellow.500}',
          borderColor: '{yellow.500}',
        },
        background: 'color-mix(in srgb, {yellow.500}, transparent 84%)',
        borderColor: 'transparent',
        closeButton: {
          focusRing: {
            color: '{focus.ring.color}',
            shadow:
              '0 0 0 0.2rem color-mix(in srgb, {yellow.500}, transparent 80%)',
          },
          hoverBackground: 'rgba(255, 255, 255, 0.05)',
        },
      },
      error: {
        color: '{red.500}',
        shadow: 'none',
        simple: {
          color: '{red.500}',
        },
        outlined: {
          color: '{red.500}',
          borderColor: '{red.500}',
        },
        background: 'color-mix(in srgb, {red.500}, transparent 84%)',
        borderColor: 'transparent',
        closeButton: {
          focusRing: {
            color: '{focus.ring.color}',
            shadow:
              '0 0 0 0.2rem color-mix(in srgb, {red.500}, transparent 80%)',
          },
          hoverBackground: 'rgba(255, 255, 255, 0.05)',
        },
      },
      success: {
        color: '{green.500}',
        shadow: 'none',
        simple: {
          color: '{green.500}',
        },
        outlined: {
          color: '{green.500}',
          borderColor: '{green.500}',
        },
        background: 'color-mix(in srgb, {green.500}, transparent 84%)',
        borderColor: 'transparent',
        closeButton: {
          focusRing: {
            color: '{focus.ring.color}',
            shadow:
              '0 0 0 0.2rem color-mix(in srgb, {green.500}, transparent 80%)',
          },
          hoverBackground: 'rgba(255, 255, 255, 0.05)',
        },
      },
      contrast: {
        color: '{surface.950}',
        shadow: 'none',
        simple: {
          color: '{surface.0}',
        },
        outlined: {
          color: '{surface.0}',
          borderColor: '{surface.0}',
        },
        background: '{surface.0}',
        borderColor: 'transparent',
        closeButton: {
          focusRing: {
            color: '{focus.ring.color}',
            shadow:
              '0 0 0 0.2rem color-mix(in srgb, {surface.950}, transparent 80%)',
          },
          hoverBackground: '{surface.100}',
        },
      },
      secondary: {
        color: '{surface.300}',
        shadow: 'none',
        simple: {
          color: '{surface.400}',
        },
        outlined: {
          color: '{surface.400}',
          borderColor: '{surface.400}',
        },
        background: '{surface.800}',
        borderColor: 'transparent',
        closeButton: {
          focusRing: {
            color: '{focus.ring.color}',
            shadow:
              '0 0 0 0.2rem color-mix(in srgb, {surface.300}, transparent 80%)',
          },
          hoverBackground: '{surface.700}',
        },
      },
    },
    light: {
      info: {
        color: '{nash.midnightBlue.700}',
        shadow: 'none',
        simple: {
          color: '{nash.midnightBlue.700}',
        },
        outlined: {
          color: '{nash.midnightBlue.700}',
          borderColor: '{nash.midnightBlue.700}',
        },
        background: '{nash.fern.100}',
        borderColor: '{nash.midnightBlue.700}',
        closeButton: {
          focusRing: {
            color: '{nash.midnightBlue.700}',
            shadow: '0 0 0 0.2rem {blue.200}',
          },
          hoverBackground: '{nash.midnightBlue.100}',
        },
      },
      warn: {
        color: '{nash.mustard.650}',
        shadow: 'none',
        simple: {
          color: '{nash.mustard.650}',
        },
        outlined: {
          color: '{nash.mustard.650}',
          borderColor: '{nash.mustard.650}',
        },
        background: 'color-mix(in srgb,{yellow.50}, transparent 5%)',
        borderColor: '{nash.mustard.650}',
        closeButton: {
          focusRing: {
            color: '{nash.mustard.650}',
            shadow: '0 0 0 0.2rem {yellow.200}',
          },
          hoverBackground: '{nash.mustard.100}',
        },
      },
      error: {
        color: '{nash.tigerlily.800}',
        shadow: 'none',
        simple: {
          color: '{nash.tigerlily.800}',
        },
        outlined: {
          color: '{nash.tigerlily.800}',
          borderColor: '{red.600}',
        },
        background: 'color-mix(in srgb, {red.50}, transparent 5%)',
        borderColor: '{nash.tigerlily.800}',
        closeButton: {
          focusRing: {
            color: '{nash.tigerlily.800}',
            shadow: '0 0 0 0.2rem {red.200}',
          },
          hoverBackground: '{nash.tigerlily.100}',
        },
      },
      success: {
        color: '{green.600}',
        shadow: 'none',
        simple: {
          color: '{nash.fern.600}',
        },
        outlined: {
          color: '{nash.fern.600}',
          borderColor: '{nash.fern.600}',
        },
        background: 'color-mix(in srgb, {green.50}, transparent 5%)',
        borderColor: '{nash.fern.600}',
        closeButton: {
          focusRing: {
            color: '{nash.fern.600}',
            shadow: 'none',
          },
          hoverBackground: '{green.100}',
        },
      },
      contrast: {
        color: '{surface.50}',
        shadow: 'none',
        simple: {
          color: '{surface.900}',
        },
        outlined: {
          color: '{surface.900}',
          borderColor: '{surface.900}',
        },
        background: '{surface.900}',
        borderColor: 'transparent',
        closeButton: {
          focusRing: {
            color: '{focus.ring.color}',
            shadow: '0 0 0 0.2rem {surface.400}',
          },
          hoverBackground: '{surface.800}',
        },
      },
      secondary: {
        color: '{surface.600}',
        shadow: 'none',
        simple: {
          color: '{surface.500}',
        },
        outlined: {
          color: '{surface.500}',
          borderColor: '{surface.500}',
        },
        background: '{surface.100}',
        borderColor: 'transparent',
        closeButton: {
          focusRing: {
            color: '{focus.ring.color}',
            shadow: '0 0 0 0.2rem {surface.200}',
          },
          hoverBackground: '{surface.200}',
        },
      },
    },
  },
} satisfies MessageDesignTokens
